import { useMemo } from 'react';
// import type { Deal } from '@/models/registry'
import DealDeliveryPreferenceBadge from '@/components/deal/delivery-preference/deal-delivery-preference-badge';
import DeliveryQueueBadge from '@/components/deal/delivery-queue/delivery-queue-badge';
import FinanceManagerBadge from '@/components/deal/finance-manager/finance-manager-badge';
import DealLenderBadge from '@/components/deal/lenders/deal-lender-bagde';
import useLenderMutations from '@/components/deal/lenders/use-lender-mutations.ts';
import { DealPriorityBadge } from '@/components/deal/priority/deal-priority-badge';
import SalesRepBadge from '@/components/deal/sales-reps/sales-rep-badge';
import { DealStageBadge } from '@/components/deal/stage/deal-stage-badge';
import { DealStatusBadge } from '@/components/deal/status/deal-status-badge';
import { PDKDeliveryDateIcon } from '@/components/ui/pdk-icons';
import type { HotkeyDialogBox } from '@/components/ux/all-list-and-board/use-all-list-and-board-hotkeys';
import { toast } from '@/hooks/use-toast.ts';
import type { Deal } from '@/models/registry';
import { compact } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

interface UseDealActionsProps {
  deal: Deal;
  dealShortId?: Deal['short_id'];
  setOpenDialog: (dialog: HotkeyDialogBox | null) => void;
  setTradeIn?: (tradeIn: Deal['trades'][0] | null) => void;
  setCreateLenderDialogIsOpen?: (open: boolean) => void;
}

// const TRADE_IN_MENU_ITEM_PREFIX: string = 'trade-in-';

export const useDealActions = ({
  deal,
  dealShortId,
  setOpenDialog,
  setTradeIn,
  setCreateLenderDialogIsOpen,
}: UseDealActionsProps) => {
  const { createLenderMutation } = setCreateLenderDialogIsOpen
    ? useLenderMutations({
        dealId: deal?.id,
        dealShortId,
        setDialogIsOpen: setCreateLenderDialogIsOpen,
        onCreateSuccess: () => {
          toast({
            title: `Lender successfully created and added to deal`,
          });
        },
        onCreateError: (error) => {
          toast({
            title: `Error creating lender`,
            description: error.message,
          });
        },
      })
    : { createLenderMutation: null };

  const dealActions = useMemo(
    () =>
      compact([
        {
          label: 'Set priority',
          value: 'set priority',
          icon: (
            <DealPriorityBadge
              value={deal.priority}
              hideCopy
              className="size-3.5"
            />
          ),
          onClick: () => setOpenDialog('priority'),
        },
        {
          label: 'Set status',
          value: 'set status',
          icon: (
            <DealStatusBadge
              value={deal.status}
              hideCopy
              className="size-3.5"
            />
          ),
          onClick: () => setOpenDialog('status'),
        },
        {
          label: 'Set contract status',
          value: 'set contract status',
          icon: (
            <DealStageBadge value={deal.stage} hideCopy className="size-3.5" />
          ),
          onClick: () => setOpenDialog('contract-status'),
        },
        deal.vehicle_listing &&
        deal.delivery_date &&
        deal.date_added_to_customer_delivery_queue
          ? {
              label: 'Set delivery queued',
              value: 'set delivery queued',
              icon: (
                <DeliveryQueueBadge
                  pickupDate={deal.pickup_date}
                  delivery_date={deal.delivery_date}
                  // vehicle={deal.vehicle_listing}
                  date_added={deal.date_added_to_customer_delivery_queue}
                  hideCopy
                  className="size-3.5"
                />
              ),
              onClick: () => setOpenDialog('delivery-queue'),
            }
          : null,
        {
          label: 'Set delivery date',
          value: 'set delivery date',
          icon: <PDKDeliveryDateIcon className="size-3.5" />,
          onClick: () => setOpenDialog('delivery-date'),
        },
        {
          label: 'Set finance manager',
          value: 'set finance manager',
          icon: (
            <FinanceManagerBadge
              financeManager={deal.finance_manager}
              hideNameAndCopy
              useAbbreviations
              className="size-3.5"
            />
          ),
          onClick: () => setOpenDialog('finance-manager'),
        },
        {
          label: 'Set 1st sales rep',
          value: 'set 1st sales rep',
          icon: (
            <SalesRepBadge
              salesRep={deal.primary_sales_rep}
              hideCopy
              useAbbreviations
              className="size-3.5"
            />
          ),
          onClick: () => setOpenDialog('sales-rep-1'),
        },
        {
          label: 'Set 2nd sales rep',
          value: 'set 2nd sales rep',
          icon: (
            <SalesRepBadge
              salesRep={deal.secondary_sales_rep}
              hideCopy
              useAbbreviations
              className="size-3.5"
            />
          ),
          onClick: () => setOpenDialog('sales-rep-2'),
        },
        {
          label: 'Set delivery preference',
          value: 'set delivery preference',
          icon: (
            <DealDeliveryPreferenceBadge
              value={deal.delivery_preference}
              hideCopy
              className="size-3.5"
            />
          ),
          onClick: () => setOpenDialog('delivery-preference'),
        },
        {
          label: 'Set lender',
          value: 'set lender',
          icon: (
            <DealLenderBadge
              lender={deal.lender}
              hideCopy
              className="size-3.5"
            />
          ),
          onClick: () => setOpenDialog('set-lender'),
        },
        setCreateLenderDialogIsOpen
          ? {
              label: 'Create lender',
              value: 'create lender',
              icon: (
                <DealLenderBadge
                  lender={deal.lender}
                  hideCopy
                  className="size-3.5"
                />
              ),
              onClick: () => setCreateLenderDialogIsOpen(true),
            }
          : null,
        // TODO: Add this back when the trade in functionality is built out
        // ...(deal?.trade_ins?.map((tradeIn) => {
        //   const tradeInLabel = `${tradeIn.year} ${tradeIn.make} ${tradeIn.model}`;
        //   return {
        //     label: tradeInLabel,
        //     value: `${tradeInLabel} ${TRADE_IN_MENU_ITEM_PREFIX}${tradeIn.id}`,
        //     icon: <PDKVehicleTradeInIcon className="size-3.5" />,
        //     onClick: () => setTradeIn(tradeIn),
        //   };
        // }) || []),
      ]),
    [
      deal.priority,
      deal.status,
      deal.stage,
      deal.delivery_preference,
      deal.delivery_date,
      deal.date_added_to_customer_delivery_queue,
      deal.pickup_date,
      deal.finance_manager,
      deal.primary_sales_rep,
      deal.secondary_sales_rep,
      deal.lender,
      deal.vehicle_listing,
      setOpenDialog,
      setTradeIn,
      setCreateLenderDialogIsOpen,
    ],
  );

  const handleAction = (value: string) => {
    const action = dealActions.find((act) => act.value === value);
    if (action) action.onClick();
  };

  const handleCreateLender = setCreateLenderDialogIsOpen
    ? ({
        nameValue,
        emailValue,
        phoneValue,
        lenderTypeValue,
      }: {
        nameValue: string;
        emailValue: string;
        phoneValue: string;
        lenderTypeValue: 'bank' | 'credit_union';
      }) => {
        createLenderMutation?.mutate({
          lender: {
            id: uuidv4(),
            name: nameValue,
            email: emailValue,
            phone: phoneValue,
            lender_type: lenderTypeValue,
          },
        });
      }
    : undefined;

  return {
    dealActions,
    handleAction,
    handleCreateLender,
  };
};
